import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import { Spinner, Tabs } from "@opendate-app/setlist";

import { useLandingPage } from "./LandingPageBuilderContainer";

const DEVICE_WIDTHS = { desktop: 820, mobile: 390 };

const styles = {
  wrapper: { display: "flex", flexDirection: "column", height: "100%" },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px",
    borderBottom: "1px solid var(--sl-border-secondary)",
    background: "var(--sl-bg-primary)",
  },
  stage: { flex: 1, minHeight: 0, overflow: "auto", display: "flex", justifyContent: "center", padding: 24 },
  frame: (width) => ({
    width,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "var(--sl-radius-xl, 12px)",
    overflow: "hidden",
    background: "var(--sl-bg-primary)",
  }),
  chrome: {
    padding: "8px 12px",
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
    borderBottom: "1px solid var(--sl-border-secondary)",
    background: "var(--sl-bg-secondary)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  body: { position: "relative", flex: 1, minHeight: 480 },
  iframe: { width: "100%", height: "100%", minHeight: 480, border: 0, display: "block" },
  overlay: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--sl-bg-primary)", opacity: 0.8 },
  redirect: { padding: 32, fontSize: 14, color: "var(--sl-text-secondary)" },
};

const PagePreview = () => {
  const { page, props, previewView, setPreviewView } = useLandingPage();
  const [device, setDevice] = useState("desktop");
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);
  const basePath = `/teams/${props.team.id}/pages/${page.id}`;
  const redirecting = page.settings?.after === "redirect";

  const fetchPreview = useRef(null);
  fetchPreview.current = () => {
    const request = ++latest.current;
    setLoading(true);
    axios.post(`${basePath}/render_html`)
      .then(({ data }) => {
        if (request === latest.current) setHtml(data);
      })
      .catch(() => {})
      .finally(() => {
        if (request === latest.current) setLoading(false);
      });
  };

  const debounced = useRef(debounce(() => fetchPreview.current(), 600)).current;

  useEffect(() => {
    debounced();
  }, [page]);

  useEffect(() => () => debounced.cancel(), []);

  const view = redirecting && previewView === "thanks" ? "redirect" : previewView;
  const srcDoc = html ? (previewView === "thanks" ? html.thanks_html : html.html) : "";

  return (
    <div style={styles.wrapper}>
      <div style={styles.toolbar}>
        <Tabs
          variant="button-minimal"
          size="sm"
          collapseBelow={0}
          aria-label="Preview page"
          value={previewView}
          onChange={setPreviewView}
          items={[
            { value: "page", label: "Page" },
            { value: "thanks", label: redirecting ? "After submit" : "Thank-you page" },
          ]}
        />
        <Tabs
          variant="button-minimal"
          size="sm"
          collapseBelow={0}
          aria-label="Preview device"
          value={device}
          onChange={setDevice}
          items={[
            { value: "desktop", label: "Desktop" },
            { value: "mobile", label: "Mobile" },
          ]}
        />
      </div>

      <div style={styles.stage}>
        <div style={styles.frame(DEVICE_WIDTHS[device])}>
          <div style={styles.chrome}>{`${props.publicBaseUrl}/p/${page.slug || ""}`}</div>
          <div style={styles.body}>
            {view === "redirect" ? (
              <div style={styles.redirect}>Fans are sent to {page.settings.redirect_url || "your redirect URL"}</div>
            ) : (
              <iframe title="Page preview" style={styles.iframe} srcDoc={srcDoc} sandbox="" />
            )}
            {loading && view !== "redirect" && (
              <div style={styles.overlay}>
                <Spinner aria-label="Loading preview" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagePreview;
